import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  RightOutlined,
  SendOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { NAV, TASK_TYPES, eventLabel, statusLabel } from "@wms/domain";
import type { Dashboard, ReportResult } from "@wms/domain";
import { Alert, List, Progress, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { attentionApi } from "../api/attention";
import { api, type Page } from "../api/client";
import { formatDate, formatQty } from "../api/format";
import { useAuthStore } from "../auth/store";
import { MetricTile } from "../components/MetricTile";
import { PageHeader } from "../components/PageHeader";
import { AttentionList } from "./AttentionList";
import { Desktop, type WidgetView } from "./Desktop";
import { DataTime, WidgetState } from "./widgetParts";

/** The widgets this screen draws — the staff catalogue of the server (common/workspace.py). */
export const STAFF_WIDGET_CODES = ["receipts", "orders", "tasks", "stock", "attention", "events", "quick_links", "my_tasks", "expiry", "zone_fill"] as const;

/** CW-13: «мини-пример» of the catalogue — what exactly the widget shows. */
const PREVIEWS: Record<string, string> = {
  receipts: "три числа — поступлений на сегодня, приёмка в работе, принято сегодня",
  orders: "три числа — заказов на сегодня, в отборе, отгружено сегодня",
  tasks: "пять чисел — к выполнению, выполняется, с проблемой, выполнено сегодня, исполнителей на смене",
  stock: "сколько товаров на остатке и в скольких ячейках",
  attention: "номер документа или объекта, причину, склад, состояние и ссылку «Открыть»",
  events: "последние события: время, событие, номер, новый статус",
  quick_links: "кнопки разделов, открытых вашей роли",
  my_tasks: "до пяти ваших незавершённых задач: номер, что сделать, статус",
  expiry: "до пяти партий: годен до, сколько дней осталось, остаток",
  zone_fill: "зоны по заполненности: занято ячеек из всех, процент, признак «критично»",
};
// the shortcuts of «Быстрые переходы»: the menu items themselves, each shown only with its right and option
const QUICK_LINKS = ["/documents", "/work", "/stock-summary", "/dispatcher"];

/** UXW-26 / CW-18: the user and the working organization are part of every key — numbers never pass between them. */
function useScopeKey(): string[] {
  const user = useAuthStore((s) => s.me?.user.id ?? "-");
  const company = useAuthStore((s) => s.company?.id ?? "-");
  return [user, company];
}

/** CW-16: every widget of `/dashboard` reads this one query — one request, one cache, one refresh a minute. */
function useDashboard() {
  const scope = useScopeKey();
  const allowed = useAuthStore((s) => s.can("analytics.dashboard.read"));
  return useQuery({
    queryKey: ["dashboard", "desktop", ...scope],
    queryFn: () => api.get<Dashboard>("/dashboard"),
    enabled: allowed,
    refetchInterval: 60000,
  });
}

function DashboardTime() {
  const q = useDashboard();
  return <DataTime at={q.data?.generated_at} fetching={q.isFetching} />;
}

function Tiles({ children }: { children: ReactNode }) {
  return <div className="wms-widget-tiles">{children}</div>;
}

// UXW-18: the tiles, numbers, links and hints of the page of HEAD, widget by widget
function ReceiptsWidget() {
  const q = useDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <Tiles>
          <MetricTile op="receive" title="Поступлений на сегодня" hint="Документы поступления с датой «сегодня», кроме отменённых" value={d.receipts.planned_today} to="/expected_receipts" icon={<InboxOutlined />} />
          <MetricTile op="receive" title="Приёмка в работе" value={d.receipts.in_progress} to="/expected_receipts" icon={<PlayCircleOutlined />} tone="orange" />
          <MetricTile op="receive" title="Принято сегодня" hint="Поступления, приёмка которых завершена сегодня" value={d.receipts.accepted_today} to="/expected_receipts" icon={<CheckCircleOutlined />} tone="green" />
        </Tiles>
      )}
    </WidgetState>
  );
}

function OrdersWidget() {
  const q = useDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <Tiles>
          <MetricTile op="ship" title="Заказов на сегодня" hint="Заказы на отгрузку с датой «сегодня», кроме отменённых" value={d.orders.planned_today} to="/shipment_orders" icon={<SendOutlined />} tone="violet" />
          <MetricTile op="ship" title="Заказы в отборе" value={d.orders.picking} to="/shipment_orders" icon={<ThunderboltOutlined />} tone="orange" />
          <MetricTile op="ship" title="Отгружено сегодня" hint="Заказы, отгруженные сегодня" value={d.orders.shipped_today} to="/shipment_orders" icon={<CheckCircleOutlined />} tone="green" />
        </Tiles>
      )}
    </WidgetState>
  );
}

function TasksWidget() {
  const q = useDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <Tiles>
          <MetricTile title="Задач к выполнению" value={d.tasks.ready} to="/dispatcher" icon={<ClockCircleOutlined />} />
          <MetricTile title="Задач выполняется" value={d.tasks.in_progress} to="/dispatcher" icon={<PlayCircleOutlined />} tone="orange" />
          <MetricTile title="Задач с проблемой" value={d.tasks.problem} to="/dispatcher" icon={<WarningOutlined />} tone={d.tasks.problem ? "red" : "gray"} />
          <MetricTile title="Выполнено сегодня" value={d.tasks.done_today} to="/reports/productivity" icon={<CheckCircleOutlined />} tone="green" />
          <MetricTile title="Исполнителей на смене" value={d.tasks.executors} to="/dispatcher" icon={<TeamOutlined />} tone="violet" />
        </Tiles>
      )}
    </WidgetState>
  );
}

function StockWidget() {
  const q = useDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <Tiles>
          {/* one count of products and one of cells — quantities of different units are never added up (UXW-18) */}
          <MetricTile title="Товаров на остатке" value={d.stock.objects} to="/stock-summary" icon={<DatabaseOutlined />} tone="gray" suffix={`в ${d.stock.locations} яч.`} />
        </Tiles>
      )}
    </WidgetState>
  );
}

function useAttentionWidget() {
  const scope = useScopeKey();
  return useQuery({
    queryKey: ["dashboard", "attention", "widget", ...scope],
    queryFn: () => attentionApi.get("staff", { per_kind: 3, limit: 60 }),
    refetchInterval: 60000,
  });
}

/** UXW-27…29: the exact bases; when the list cannot be read, the aggregated warnings of HEAD stay as a fallback. */
function AttentionWidget() {
  const q = useAttentionWidget();
  const dashboard = useDashboard();
  const can = useAuthStore((s) => s.can);
  const alerts = q.isError && !q.data ? (dashboard.data?.alerts ?? []) : [];
  return (
    <>
      <WidgetState source={q}>{q.data && <AttentionList cabinet="staff" page={q.data} moreHref={(kind) => `/attention?kind=${encodeURIComponent(kind)}`} />}</WidgetState>
      {alerts.map((a) => (
        <Alert
          key={a.kind}
          type={a.level === "danger" ? "error" : a.level}
          showIcon
          style={{ marginTop: 8, borderRadius: 10 }}
          message={a.link.startsWith("/reports") && !can("analytics.reports.read") ? a.text : <Link to={a.link}>{a.text}</Link>}
        />
      ))}
    </>
  );
}

function AttentionExtra() {
  const q = useAttentionWidget();
  return (
    <>
      <DataTime at={q.data?.generated_at} fetching={q.isFetching} />
      <Link to="/attention" data-testid="attention-all">
        Все
      </Link>
    </>
  );
}

function EventsWidget() {
  const q = useDashboard();
  return (
    <WidgetState source={q} empty={q.data?.recent_events.length === 0} emptyText="Событий пока нет">
      {q.data && (
        <List
          size="small"
          dataSource={q.data.recent_events}
          renderItem={(e) => (
            <List.Item style={{ paddingInline: 0 }}>
              <Typography.Text type="secondary" style={{ width: 92, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                {dayjs(e.created_at).format("DD.MM HH:mm")}
              </Typography.Text>
              <Tag style={{ marginRight: 8 }}>{eventLabel(e.type)}</Tag>
              <Typography.Text style={{ flex: 1 }} ellipsis>
                {String(e.payload.number ?? e.payload.code ?? e.payload.title ?? "")}
              </Typography.Text>
              {e.payload.to ? <Typography.Text type="secondary">→ {statusLabel(e.payload.to)}</Typography.Text> : e.payload.status ? <Typography.Text type="secondary">{statusLabel(e.payload.status)}</Typography.Text> : null}
            </List.Item>
          )}
        />
      )}
    </WidgetState>
  );
}

function QuickLinksWidget() {
  const can = useAuthStore((s) => s.can);
  const option = useAuthStore((s) => s.option);
  const items = NAV.flatMap((section) => section.items)
    .filter((item) => item.kind === "page" && QUICK_LINKS.includes(item.key) && can(item.permission) && (!item.option || option(item.option)))
    .filter((item, index, all) => all.findIndex((other) => other.key === item.key) === index)
    .sort((a, b) => QUICK_LINKS.indexOf(a.key) - QUICK_LINKS.indexOf(b.key));
  if (items.length === 0) return <Typography.Text type="secondary">Разделов для быстрого перехода у вашей роли нет.</Typography.Text>;
  return (
    <div className="wms-widget-links">
      {items.map((item) => (
        <Link key={item.key} to={item.key} className="wms-hub-card" data-testid={`quick-link-${item.key.slice(1)}`}>
          <span>{item.label}</span>
          <RightOutlined />
        </Link>
      ))}
    </div>
  );
}

interface TaskRow {
  id: string;
  number: string;
  type: string;
  status: string;
  planned_at?: string | null;
}

function useMyTasks() {
  const scope = useScopeKey();
  const userId = useAuthStore((s) => s.me?.user.id);
  return useQuery({
    queryKey: ["tasks", "desktop-mine", ...scope],
    // the list of «Задачи» itself with its server filter: the tasks assigned to me that are not finished
    queryFn: () =>
      api.get<Page<TaskRow>>("/tasks", {
        page: 1,
        size: 5,
        filter: {
          and: [
            { field: "assignee_id", op: "eq", value: userId },
            { field: "status", op: "nin", value: ["done", "cancelled"] },
          ],
        },
      }),
    enabled: Boolean(userId),
    refetchInterval: 60000,
  });
}

function MyTasksWidget() {
  const q = useMyTasks();
  return (
    <WidgetState source={q} empty={q.data?.items.length === 0} emptyText="Назначенных вам незавершённых задач нет">
      {q.data && (
        <div className="wms-widget-list">
          {q.data.items.map((task) => (
            <div key={task.id} className="wms-widget-list-row" data-testid={`my-task-${task.number}`}>
              <Link to={`/tasks/${task.id}`} className="wms-attention-number">
                {task.number}
              </Link>
              <span className="wms-grow">{TASK_TYPES[task.type as keyof typeof TASK_TYPES] ?? task.type}</span>
              <Tag>{statusLabel(task.status)}</Tag>
            </div>
          ))}
          {q.data.total > q.data.items.length && <Typography.Text type="secondary">Показаны 5 из {q.data.total}</Typography.Text>}
        </div>
      )}
    </WidgetState>
  );
}

function MyTasksExtra() {
  const q = useMyTasks();
  return <DataTime at={q.dataUpdatedAt || null} fetching={q.isFetching} />;
}

function useReport(code: string, params?: Record<string, unknown>) {
  const scope = useScopeKey();
  return useQuery({
    queryKey: ["reports", code, "desktop", params ?? {}, ...scope],
    queryFn: () => api.get<ReportResult>(`/reports/${code}`, params ? { params } : undefined),
    refetchInterval: 300000,
  });
}

function ExpiryWidget() {
  const q = useReport("batches_expiry", { days: 30 });
  const rows = (q.data?.rows ?? []).slice(0, 5);
  return (
    <WidgetState source={q} empty={q.data?.rows.length === 0} emptyText="Партий, срок которых истекает в ближайшие 30 дней, нет">
      <div className="wms-widget-list">
        {rows.map((row, index) => {
          const left = Number(row.days_left);
          return (
            <div key={`${String(row.batch)}-${index}`} className="wms-widget-list-row">
              <span className="wms-attention-number">{String(row.batch ?? "")}</span>
              <span className="wms-grow">
                {String(row.object_code ?? "")} {String(row.object_name ?? "")}
              </span>
              <span>годен до {formatDate(row.expiry_date)}</span>
              <Tag color={left < 0 ? "error" : "warning"}>{left < 0 ? "просрочено" : `${left} дн.`}</Tag>
              <span>остаток {formatQty(row.quantity)}</span>
            </div>
          );
        })}
        {(q.data?.total ?? 0) > rows.length && <Typography.Text type="secondary">Показаны {rows.length} из {q.data?.total}</Typography.Text>}
      </div>
    </WidgetState>
  );
}

function ZoneFillWidget() {
  const q = useReport("zone_fill");
  const rows = [...(q.data?.rows ?? [])].sort((a, b) => Number(b.percent) - Number(a.percent)).slice(0, 6);
  return (
    <WidgetState source={q} empty={q.data?.rows.length === 0} emptyText="Зон с ячейками пока нет">
      <div className="wms-widget-list">
        {rows.map((row) => (
          <div key={String(row.zone)} className="wms-widget-list-row">
            <span className="wms-grow">
              <b>{String(row.zone)}</b> {String(row.name ?? "")}
            </span>
            <span>
              занято {String(row.occupied)} из {String(row.locations)}
            </span>
            <Progress percent={Number(row.percent)} size="small" status={row.critical ? "exception" : "normal"} style={{ width: 140, margin: 0 }} />
            {Boolean(row.critical) && <Tag color="error">критично</Tag>}
          </div>
        ))}
      </div>
    </WidgetState>
  );
}

function ReportExtra({ code, params, title }: { code: string; params?: Record<string, unknown>; title: string }) {
  const q = useReport(code, params);
  const can = useAuthStore((s) => s.can);
  return (
    <>
      <DataTime at={q.dataUpdatedAt || null} fetching={q.isFetching} />
      {can("analytics.reports.read") && <Link to={`/reports/${code}`}>{title}</Link>}
    </>
  );
}

function render(code: string): WidgetView {
  switch (code) {
    case "receipts":
      return { body: <ReceiptsWidget />, extra: <DashboardTime /> };
    case "orders":
      return { body: <OrdersWidget />, extra: <DashboardTime /> };
    case "tasks":
      return { body: <TasksWidget />, extra: <DashboardTime /> };
    case "stock":
      return { body: <StockWidget />, extra: <DashboardTime /> };
    case "attention":
      return { body: <AttentionWidget />, extra: <AttentionExtra /> };
    case "events":
      return {
        body: <EventsWidget />,
        extra: (
          <>
            <DashboardTime />
            <Link to="/reports">Отчёты</Link>
          </>
        ),
      };
    case "quick_links":
      return { body: <QuickLinksWidget /> };
    case "my_tasks":
      return { body: <MyTasksWidget />, extra: <MyTasksExtra /> };
    case "expiry":
      return { body: <ExpiryWidget />, extra: <ReportExtra code="batches_expiry" params={{ days: 30 }} title="Отчёт" /> };
    case "zone_fill":
      return { body: <ZoneFillWidget />, extra: <ReportExtra code="zone_fill" title="Отчёт" /> };
    default:
      return { body: null };
  }
}

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-18/19): the staff desktop from widgets — the blocks of the page of HEAD first,
 * the extensions from the catalogue. `legacy` is that page itself: shown when the own desktop cannot be read.
 */
export function StaffDesktop({ legacy }: { legacy: ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const allowed = useAuthStore((s) => s.can("analytics.dashboard.read"));
  const dashboard = useDashboard();
  const hour = new Date().getHours();
  const greeting = hour < 6 ? "Доброй ночи" : hour < 12 ? "Доброе утро" : hour < 18 ? "Добрый день" : "Добрый вечер";
  const timezone = dashboard.data?.timezone;
  const header = (
    <>
      <PageHeader
        title={`${greeting}, ${me?.user.full_name?.split(" ")[0] ?? ""}`}
        subtitle={`${dayjs().format("dddd, D MMMM YYYY")}${timezone ? ` · «сегодня» в показателях — по времени склада (${timezone})` : ""}`}
      />
      {!allowed && <Typography.Paragraph type="secondary">Показатели рабочего стола недоступны для вашей роли.</Typography.Paragraph>}
    </>
  );
  return <Desktop cabinet="staff" codes={STAFF_WIDGET_CODES} render={render} previews={PREVIEWS} header={header} fallback={legacy} />;
}
